import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import type { AppState } from '../../reducers/root.reducer';
import { createSelector } from 'reselect';
import AdminProjectService from '../services/AdminProjectService';
import IBaseState from '../../types/IBaseState';
import ProjectPackage from '../types/project/ProjectPackage';
import ProjectParticipant from '../types/project/ProjectParticipant';
import ProjectTranslation from '../types/project/ProjectTranslation';
import IAdminProject from '../types/IAdminProject';
import ProjectJobMatchQuestionnaire from '../types/jobMatch/ProjectJobMatchQuestionnaire';
import ProjectJobMatchScale from '../types/jobMatch/ProjectJobMatchScale';
import AdminProjectAdminsService from '../services/AdminProjectAdminsService';
import ProjectAdminType from '../types/ProjectAdminType';
import UploadState from '../../types/UploadState';
import AdminReportsService from '../services/AdminReportsService';
import Report from '../types/Report';
import AdminInviteService from '../services/AdminInviteService';
import ProjectInvitation from '../types/invitation/ProjectInvitation';
import Participant360 from '../types/project/360/Participant360';

const NAME = 'AdminProject';

const createProject = createAsyncThunk(
    `${NAME}/createProject`,
    AdminProjectService.createProject,
);

const getProject = createAsyncThunk(
    `${NAME}/getProject`,
    AdminProjectService.getProject,
);
const saveProject = createAsyncThunk(
    `${NAME}/saveProject`,
    AdminProjectService.saveProject,
);

const getPackages = createAsyncThunk(
    `${NAME}/getPackages`,
    AdminProjectService.getPackages,
);
const selectPackage = createAsyncThunk(
    `${NAME}/selectPackage`,
    AdminProjectService.selectPackage,
);

const getQuestionnaires = createAsyncThunk(
    `${NAME}/getQuestionnaires`,
    AdminProjectService.getQuestionnaires,
);
const selectQuestionnaires = createAsyncThunk(
    `${NAME}/selectQuestionnaires`,
    AdminProjectService.selectQuestionnaires,
);
const updateQuestionnaires = createAsyncThunk(
    `${NAME}/updateQuestionnaires`,
    AdminProjectService.updateQuestionnaires,
);

const getParticipants = createAsyncThunk(
    `${NAME}/getParticipants`,
    AdminProjectService.getParticipants,
);
const addParticipant = createAsyncThunk(
    `${NAME}/addParticipant`,
    AdminProjectService.addParticipant,
);
const deleteParticipant = createAsyncThunk(
    `${NAME}/deleteParticipant`,
    AdminProjectService.deleteParticipant,
);
const getTranslations = createAsyncThunk(
    `${NAME}/getTranslations`,
    AdminProjectService.getTranslations,
);
const addLanguage = createAsyncThunk(
    `${NAME}/addLanguage`,
    AdminProjectService.addLanguage,
);
const removeLanguage = createAsyncThunk(
    `${NAME}/removeLanguage`,
    AdminProjectService.removeLanguage,
);
const saveTranslations = createAsyncThunk(
    `${NAME}/saveTranslations`,
    AdminProjectService.saveTranslations,
);
const saveTranslation = createAsyncThunk(
    `${NAME}/saveTranslation`,
    AdminProjectService.saveTranslation,
);
const getQuestionnaireScales = createAsyncThunk(
    `${NAME}/getQuestionnaireScales`,
    AdminProjectService.getQuestionnaireScales,
);

const updateQuestionnaireScales = createAsyncThunk(
    `${NAME}/updateQuestionnaireScales`,
    AdminProjectService.updateQuestionnaireScales,
);

const getProjectParticipants = createAsyncThunk(
    `${NAME}/getProjectParticipants`,
    AdminProjectService.getParticipants,
);

const getProjectStatistics = createAsyncThunk(
    `${NAME}/getProjectStatistics`,
    AdminProjectService.getProjectStatistics,
);

const getAdmins = createAsyncThunk(
    `${NAME}/getAdmins`,
    AdminProjectAdminsService.getAdmins,
);
const getReports = createAsyncThunk(
    `${NAME}/getReports`,
    AdminReportsService.getReports,
);
const getJobMatchReports = createAsyncThunk(
    `${NAME}/getJobMatchReports`,
    (payload: any, thunkAPI) =>
        AdminReportsService.getReports(
            { jobMatch: true, ...payload },
            thunkAPI,
        ),
);
const getInvites = createAsyncThunk(
    `${NAME}/getInvites`,
    AdminInviteService.getInvites,
);

const deleteProject = createAsyncThunk(
    `${NAME}/deleteProject`,
    AdminProjectService.deleteProject,
);
const copyProject = createAsyncThunk(
    `${NAME}/copyProject`,
    AdminProjectService.copyProject,
);
const anonymizeProject = createAsyncThunk(
    `${NAME}/anonymizeProject`,
    AdminProjectService.anonymizeProject,
);

export type AdminProjectState = {
    project: UploadState<IAdminProject | null>;
    packages: IBaseState<ProjectPackage[]>;
    questionnaires: IBaseState<ProjectJobMatchQuestionnaire[]>;
    participants: IBaseState<ProjectParticipant[] | null>;
    translations: IBaseState<{ [key: string]: ProjectTranslation[] } | null>;
    scales: IBaseState<{ [key: number]: IBaseState<ProjectJobMatchScale[]> }>;
    projectParticipants: IBaseState<ProjectParticipant[]>;
    statistics: IBaseState<any | null>;
    admins: IBaseState<ProjectAdminType[]>;
    reports: IBaseState<Report[]>;
    jobMatchReports: IBaseState<Report[]>;
    invites: IBaseState<ProjectInvitation[]>;
};

const initialState: AdminProjectState = {
    project: { data: null, loading: false, uploading: false },
    packages: { data: [] },
    questionnaires: { data: [] },
    participants: { data: null },
    translations: { data: null },
    scales: { data: {} },
    projectParticipants: { data: [], loading: false },
    statistics: { data: null, loading: false },
    admins: { data: [], loading: false },
    reports: { data: [], loading: false },
    jobMatchReports: { data: [], loading: false },
    invites: { data: [], loading: false },
};

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(createProject.pending, (state: AdminProjectState) => {
            state.project.loading = true;
        });
        builder.addCase(
            createProject.fulfilled,
            (state: AdminProjectState, action) => {
                state.project.data = action.payload.data;
                state.project.loading = false;
            },
        );
        builder.addCase(createProject.rejected, (state: AdminProjectState) => {
            state.project.loading = false;
        });

        builder.addCase(getPackages.pending, (state: AdminProjectState) => {
            state.packages.loading = true;
        });
        builder.addCase(
            getPackages.fulfilled,
            (state: AdminProjectState, action) => {
                state.packages.data = action.payload.data;
                state.packages.loading = false;
            },
        );
        builder.addCase(getPackages.rejected, (state: AdminProjectState) => {
            state.packages.loading = false;
        });

        builder.addCase(getProject.pending, (state: AdminProjectState) => {
            state.project.loading = true;
        });
        builder.addCase(
            getProject.fulfilled,
            (state: AdminProjectState, action) => {
                state.project.data = action.payload.data;
                state.project.loading = false;
            },
        );
        builder.addCase(getProject.rejected, (state: AdminProjectState) => {
            state.project.loading = false;
            state.project.data = null;
        });

        builder.addCase(
            getQuestionnaires.pending,
            (state: AdminProjectState) => {
                state.questionnaires.loading = true;
            },
        );
        builder.addCase(
            getQuestionnaires.fulfilled,
            (state: AdminProjectState, action) => {
                state.questionnaires.data = action.payload.data;
            },
        );

        builder.addCase(
            getParticipants.fulfilled,
            (state: AdminProjectState, action) => {
                state.participants.data = action.payload.data;
            },
        );
        builder.addCase(
            addParticipant.fulfilled,
            (state: AdminProjectState, action) => {
                if (state.participants.data) {
                    state.participants.data.push(action.payload.data);
                }
            },
        );

        builder.addCase(getTranslations.pending, (state: AdminProjectState) => {
            state.translations.loading = true;
        });
        builder.addCase(
            getTranslations.fulfilled,
            (state: AdminProjectState, action) => {
                if (!state.translations.data) {
                    state.translations.data = {};
                }

                state.translations.data[action.meta.arg.locale] =
                    action.payload.data;
            },
        );
        // builder.addCase(
        //     saveTranslations.pending,
        //     (state: AdminProjectState) => {
        //         // state.translations.loading = true;
        //     },
        // );
        builder.addCase(
            saveTranslations.fulfilled,
            (state: AdminProjectState, action) => {
                const { locale } = action.meta.arg;

                if (state.translations.data?.[locale]) {
                    // state.translations.data[locale] = ;
                }
            },
        );

        builder.addCase(
            addLanguage.fulfilled,
            (state: AdminProjectState, action) => {
                if (state.project.data) {
                    state.project.data.locales = action.payload.data.locales;
                }
            },
        );

        builder.addCase(
            removeLanguage.fulfilled,
            (state: AdminProjectState, action) => {
                if (state.project.data) {
                    state.project.data.locales = action.payload.data.locales;
                }
            },
        );

        builder.addCase(
            getQuestionnaireScales.pending,
            (state: AdminProjectState, action) => {
                const { questionnaireId } = action.meta.arg;

                if (!state.scales.data?.[questionnaireId]) {
                    state.scales.data[questionnaireId] = {
                        loading: true,
                        data: [],
                    };
                } else {
                    state.scales.data[questionnaireId].loading = true;
                }
            },
        );
        builder.addCase(
            getQuestionnaireScales.fulfilled,
            (state: AdminProjectState, action) => {
                const { questionnaireId } = action.meta.arg;
                if (state.scales.data?.[questionnaireId]?.data) {
                    state.scales.data[questionnaireId].data =
                        action.payload.data;
                }
            },
        );
        builder.addCase(
            updateQuestionnaireScales.fulfilled,
            (state: AdminProjectState, action) => {
                const { questionnaireId, scaleId } = action.meta.arg;
                if (state.scales.data?.[questionnaireId]?.data?.[scaleId]) {
                    state.scales.data[questionnaireId].data[scaleId] =
                        action.payload.data;
                }
            },
        );

        builder.addCase(
            selectQuestionnaires.fulfilled,
            (state: AdminProjectState, action) => {
                state.project.data = action.payload.data;
            },
        );
        builder.addCase(
            updateQuestionnaires.fulfilled,
            (state: AdminProjectState, action) => {
                state.questionnaires.data = action.payload.data;
            },
        );

        builder.addCase(
            getProjectParticipants.pending,
            (state: AdminProjectState) => {
                state.projectParticipants.loading = true;
            },
        );
        builder.addCase(
            getProjectParticipants.fulfilled,
            (state: AdminProjectState, action) => {
                state.projectParticipants.data = action.payload.data;
                state.projectParticipants.loading = false;
            },
        );
        builder.addCase(
            getProjectParticipants.rejected,
            (state: AdminProjectState) => {
                state.projectParticipants.loading = false;
            },
        );

        builder.addCase(
            getProjectStatistics.pending,
            (state: AdminProjectState) => {
                state.statistics.loading = true;
            },
        );
        builder.addCase(
            getProjectStatistics.fulfilled,
            (state: AdminProjectState, action) => {
                state.statistics.data = action.payload.data;
            },
        );

        builder.addCase(getAdmins.pending, (state: AdminProjectState) => {
            state.admins.loading = true;
        });
        builder.addCase(
            getAdmins.fulfilled,
            (state: AdminProjectState, action) => {
                state.admins.data = action.payload.data;
            },
        );
        builder.addCase(getAdmins.rejected, (state: AdminProjectState) => {
            state.admins.data = [];
        });

        builder.addCase(saveProject.pending, (state: AdminProjectState) => {
            state.project.uploading = true;
        });
        builder.addCase(
            saveProject.fulfilled,
            (state: AdminProjectState, action) => {
                state.project.data = action.payload.data;
            },
        );

        builder.addCase(getReports.pending, (state: AdminProjectState) => {
            state.reports.loading = true;
        });
        builder.addCase(
            getReports.fulfilled,
            (state: AdminProjectState, action) => {
                state.reports.data = action.payload.data;
            },
        );
        builder.addCase(getReports.rejected, (state: AdminProjectState) => {
            state.reports.data = [];
        });
        builder.addCase(
            getJobMatchReports.pending,
            (state: AdminProjectState) => {
                state.jobMatchReports.loading = true;
            },
        );
        builder.addCase(
            getJobMatchReports.fulfilled,
            (state: AdminProjectState, action) => {
                state.jobMatchReports.data = action.payload.data;
            },
        );
        builder.addCase(
            getJobMatchReports.rejected,
            (state: AdminProjectState) => {
                state.jobMatchReports.data = [];
            },
        );

        builder.addCase(getInvites.pending, (state: AdminProjectState) => {
            state.invites.loading = true;
        });
        builder.addCase(
            getInvites.fulfilled,
            (state: AdminProjectState, action) => {
                state.invites.data = action.payload.data;
            },
        );
        builder.addCase(getInvites.rejected, (state: AdminProjectState) => {
            state.invites.data = [];
        });

        builder.addCase(
            deleteParticipant.fulfilled,
            (state: AdminProjectState, action): void => {
                if (!state.participants.data) {
                    return;
                }

                const index: number = state.participants.data.findIndex(
                    (participant: Participant360) =>
                        participant.id === action.meta.arg.participantId,
                );
                if (index >= 0) {
                    state.participants.data.splice(index, 1);
                }
            },
        );

        builder.addMatcher(
            isAnyOf(getQuestionnaires.fulfilled, getQuestionnaires.rejected),
            (state: AdminProjectState) => {
                state.questionnaires.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getParticipants.pending,
                addParticipant.pending,
                deleteParticipant.pending,
            ),
            (state: AdminProjectState) => {
                state.participants.loading = true;
            },
        );
        builder.addMatcher(
            isAnyOf(
                getParticipants.fulfilled,
                getParticipants.rejected,
                addParticipant.fulfilled,
                addParticipant.rejected,
                deleteParticipant.fulfilled,
                deleteParticipant.rejected,
            ),
            (state: AdminProjectState) => {
                state.participants.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(getTranslations.fulfilled, getTranslations.rejected),
            (state: AdminProjectState) => {
                state.translations.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getQuestionnaireScales.fulfilled,
                getQuestionnaireScales.rejected,
            ),
            (state: AdminProjectState, action) => {
                const { questionnaireId } = action.meta.arg;

                if (state.scales.data?.[questionnaireId]) {
                    state.scales.data[questionnaireId].loading = false;
                }
            },
        );

        builder.addMatcher(
            isAnyOf(
                getProjectStatistics.fulfilled,
                getProjectStatistics.rejected,
            ),
            (state: AdminProjectState) => {
                state.statistics.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(getAdmins.fulfilled, getAdmins.rejected),
            (state: AdminProjectState) => {
                state.admins.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(saveProject.fulfilled, saveProject.rejected),
            (state: AdminProjectState) => {
                state.project.uploading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(getReports.fulfilled, getReports.rejected),
            (state: AdminProjectState) => {
                state.reports.loading = false;
            },
        );
        builder.addMatcher(
            isAnyOf(getJobMatchReports.fulfilled, getJobMatchReports.rejected),
            (state: AdminProjectState) => {
                state.jobMatchReports.loading = false;
            },
        );
        builder.addMatcher(
            isAnyOf(getInvites.fulfilled, getInvites.rejected),
            (state: AdminProjectState) => {
                state.invites.loading = false;
            },
        );
    },
});

const state = (state: AppState) => state.admin.project;

const AdminProjectSlice = {
    reducer: slice.reducer,
    actions: {
        createProject,
        getProject,
        saveProject,
        selectPackage,
        getPackages,
        getQuestionnaires,
        selectQuestionnaires,
        getParticipants,
        addParticipant,
        getTranslations,
        addLanguage,
        removeLanguage,
        saveTranslation,
        saveTranslations,
        getQuestionnaireScales,
        updateQuestionnaireScales,
        updateQuestionnaires,
        getProjectParticipants,
        getProjectStatistics,
        getAdmins,
        getReports,
        getJobMatchReports,
        getInvites,
        deleteParticipant,
        deleteProject,
        copyProject,
        anonymizeProject,
    },
    state: {
        getAll: createSelector(state, (state: AdminProjectState) => state),
        getProject: createSelector(
            state,
            (state: AdminProjectState) => state.project,
        ),
        getPackages: createSelector(
            state,
            (state: AdminProjectState) => state.packages,
        ),
        getQuestionnaires: createSelector(
            state,
            (state: AdminProjectState) => state.questionnaires,
        ),
        getParticipants: createSelector(
            state,
            (state: AdminProjectState) => state.participants,
        ),
        getTranslations: createSelector(
            state,
            (state: AdminProjectState) => state.translations,
        ),
        getTranslationsByLocale: createSelector(
            [state, (state: AppState, locale: string) => locale],
            (state: AdminProjectState, locale: string) =>
                state.translations?.data?.[locale],
        ),
        getQuestionnaireScales: createSelector(
            state,
            (state: AdminProjectState) => state.scales,
        ),
        getQuestionnaireScalesByQuestionnaireId: createSelector(
            [
                state,
                (state: AppState, questionnaireId: number) => questionnaireId,
            ],
            (state: AdminProjectState, questionnaireId: number) =>
                state.scales?.data?.[questionnaireId],
        ),
        getProjectParticipants: createSelector(
            state,
            (state: AdminProjectState) => state.projectParticipants,
        ),
        getProjectStatistics: createSelector(
            state,
            (state: AdminProjectState) => state.statistics,
        ),
        getAdmins: createSelector(
            state,
            (state: AdminProjectState) => state.admins,
        ),
        getReports: createSelector(
            state,
            (state: AdminProjectState) => state.reports,
        ),
        getJobMatchReports: createSelector(
            state,
            (state: AdminProjectState) => state.jobMatchReports,
        ),
        getInvites: createSelector(
            state,
            (state: AdminProjectState) => state.invites,
        ),
    },
};

export default AdminProjectSlice;
